import PropTypes from "prop-types";
import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Typography, Button, Box, Grid, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { debounce } from "lodash";
import useConstant from "use-constant";
import { useDispatch } from "react-redux";

// Formik
import { Formik, Form, Field } from "formik";
import { TextField } from "formik-mui";
import Select from "../../components/FormikFields/Select";

//Custom
import Modal from "../../components/Modal";
import CourseTypeIcon from "../../components/Icons/CourseTypeIcon";
import CourseFlags from "../../components/Icons/CourseFlags";
import Table from "../../components/ReactTable/Table";
import { useCourses, useBatchCreateAssignments } from "../../hooks/assignments";
import { useEmployee } from "../../hooks/employees";
import usePermissions from "../../redux/selectors/usePermissions";
import DatePicker from "../../components/DatePicker/DatePicker";
import { useTableData } from "../../components/ReactTable/useTableData";
import ButtonGroup from "../../components/Buttons/ButtonGroup";
import {
  getDurationString,
  isValidDate,
  formatDate
} from "../../utils/datetime";
import { enqueueErrorSnackbar } from "../../redux/actions/snackbarActions";
import ConfirmationModal from "./CreateAssignmentsPreview";
import { useTrainingObjectTypes } from "../../hooks/trainingObjectTypes";

const hasInvalidDueDate = dueDates =>
  dueDates.filter(({ dueDate }) => !isValidDate(dueDate)).length > 0;

const getDefaultDateForTrainingObject = daysToComplete => {
  const defaultDueDate = new Date();
  defaultDueDate.setDate(defaultDueDate.getDate() + daysToComplete);
  return defaultDueDate;
};

const DateCell = ({ value, disabled, onChange }) => {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState(null);
  const [dateInput, setDateInput] = useState(null);

  useEffect(() => {
    setDateInput(value);
    setErrorMessage(
      !isValidDate(value) ? t("validationMessages.validDate") : ""
    );
  }, [value]);

  return (
    <Box minWidth={180} maxWidth={180}>
      <DatePicker
        fullWidth
        minDate={new Date()}
        disabled={disabled}
        value={value}
        onChange={newDate => setDateInput(newDate)}
        error={!!errorMessage}
        helperText={errorMessage}
        onAccept={onChange}
        onBlur={() => onChange(dateInput)}
        inputDataTestId="create-assignment-modal-due-date"
        size="small"
      />
    </Box>
  );
};

const columns = t => {
  return [
    {
      Header: t("pickableTrainingObject.type"),
      accessor: ({ courseType }) => (
        <CourseTypeIcon type={courseType} onlineOrIltOnly />
      ),
      id: "type_id"
    },
    {
      Header: t("pickableTrainingObject.number"),
      accessor: "number",
      id: "trainingObjects.number"
    },
    {
      Header: t("pickableTrainingObject.title"),
      accessor: ({ name, flags, disabled }) => (
        <>
          <Box display="flex" alignItems="center">
            <Box mr={2}>{name}</Box>
            {flags && <CourseFlags flags={flags} />}
          </Box>
          {disabled && (
            <Box fontSize="0.75rem">
              <i>{t("pickableTrainingObject.excludedByAdminNote")}</i>
            </Box>
          )}
        </>
      ),
      id: "trainingObjects.name"
    },
    {
      Header: t("pickableTrainingObject.dueDate"),
      disableSortBy: true,
      Cell: ({
        row: {
          original: { id },
          state: { dueDate },
          setState
        },
        state: { selectedRowIds }
      }) => {
        return (
          <DateCell
            value={dueDate}
            onChange={newDueDate =>
              setState(rowState => ({ ...rowState, dueDate: newDueDate }))
            }
            disabled={
              !Object.keys(selectedRowIds).find(rowId => rowId === `${id}`)
            }
          />
        );
      },
      id: "dueDate"
    },
    {
      Header: t("pickableTrainingObject.passingScore"),
      accessor: "passingScore",
      id: "trainingObjects.passingScore"
    },
    {
      Header: t("pickableTrainingObject.duration"),
      accessor: row =>
        getDurationString(row.durationHours * 60 + row.durationMinutes, t),
      id: "trainingObjects.duration"
    }
  ];
};

const TrainingObjectFilters = ({
  defaultValues,
  trainingObjectTypeOptions,
  onSubmit
}) => {
  const { t } = useTranslation();

  return (
    <Formik
      initialValues={defaultValues}
      enableReinitialize
      onSubmit={onSubmit}
    >
      {({ submitForm, setSubmitting }) => {
        const handleSubmitForm = () => {
          submitForm();
          setSubmitting(false);
        };
        const debouncedSubmit = useConstant(() =>
          debounce(handleSubmitForm, 500)
        );
        return (
          <Form>
            <Grid container spacing={2} sx={{ alignItems: "center" }}>
              <Grid item xs={12} md={4}>
                <Select
                  name="type"
                  menuItems={trainingObjectTypeOptions}
                  submitOnChange={true}
                  label={t("employees.assignments.type")}
                  disableUnderline
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <Field
                  fullWidth
                  label={t("employees.assignments.searchName")}
                  variant="filled"
                  component={TextField}
                  name={"searchName"}
                  onKeyUp={debouncedSubmit}
                  type="text"
                  disabled={false}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    disableUnderline: true
                  }}
                />
              </Grid>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};

const CreateAssignmentsModal = ({
  onClose,
  open,
  employeeID,
  presentUpdateCallback,
  assignCustomAssignments,
  isCreatingValidateAssignments,
  validate
}) => {
  const defaultFilters = {
    type: "",
    searchName: "",
    filterOutAssignedByUserId: employeeID
  };
  const { t } = useTranslation();
  const { getCourses } = useCourses("individual", employeeID);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [assignmentsToAdd, setAssignmentsToAdd] = useState([]);
  const dispatch = useDispatch();
  const {
    createAssignments,
    isCreating: isCreatingNewAssignments
  } = useBatchCreateAssignments();
  const { employee } = useEmployee(employeeID);
  const { hasPolicyPartnerAdminAccess } = usePermissions();

  const {
    data,
    paginationState,
    onStateChange,
    onApplyExternalFilters,
    isFetching,
    refreshData,
    selectedRowIds,
    rowState
  } = useTableData(getCourses, { externalFilters: defaultFilters });
  const selectedRowList = !!selectedRowIds && Object.keys(selectedRowIds);
  const enableAssignmentButton = useMemo(() => !!selectedRowList.length, [
    selectedRowIds
  ]);

  const handleClearFilters = () => {
    onApplyExternalFilters(defaultFilters);
    onClose();
  };

  const handleAddAssignmentsClick = async () => {
    const hasInvalidDate = hasInvalidDueDate(Object.values(rowState));
    if (hasInvalidDate) {
      dispatch(
        enqueueErrorSnackbar({
          message: t("employees.assignments.invalidDateErrorMsg")
        })
      );
    } else {
      const assignments = selectedRowList.reduce(
        (assignments, selectedId) => [...assignments, rowState[selectedId]],
        []
      );
      setAssignmentsToAdd(assignments);
      setShowConfirmationModal(true);
    }
  };

  const handleCreateNewAssignments = async () => {
    if (validate)
      assignCustomAssignments(
        assignmentsToAdd.map(({ dueDate, id }) => ({
          trainingObjectId: id,
          dueDate: formatDate(dueDate, "YYYY-MM-DD")
        }))
      ).then(() => {
        setShowConfirmationModal(false);
        setAssignmentsToAdd([]);
      });
    else {
      const newAssignmentsData = assignmentsToAdd.map(({ dueDate, id }) => ({
        referenceType: "Individual",
        referenceId: employeeID,
        trainingObjectId: id,
        dueDate: formatDate(dueDate, "YYYY-MM-DD"),
        op: "add"
      }));
      const operation_results = await createAssignments(newAssignmentsData);
      setShowConfirmationModal(false);
      setAssignmentsToAdd([]);
      if (operation_results) presentUpdateCallback(operation_results);
    }
    onClose();
    refreshData();
  };

  const memoColumns = useMemo(
    () =>
      hasPolicyPartnerAdminAccess
        ? columns(t)
        : columns(t).filter(x => x.id !== "trainingObjects.number"),
    []
  );

  const handleCheckRow = ({
    setState,
    original: { daysToComplete, ...rowData }
  }) => {
    // Set the dueDate in the row state to the default due date when it is checked or unchecked
    setState({
      dueDate: getDefaultDateForTrainingObject(daysToComplete),
      ...rowData
    });
  };

  const { trainingObjectTypeOptions } = useTrainingObjectTypes("assignable");

  return (
    <>
      <Modal
        disableEnforceFocus
        showCloseIcon
        maxWidth="lg"
        fullWidth
        fillHeight
        dialogTitle={
          <Typography variant="h4" color="textPrimary">
            {validate
              ? t("employees.assignments.validateAssignmentsForEmployee", {
                  name: employee?.fullName
                })
              : t("employees.assignments.assignmentsForEmployee", {
                  name: employee?.fullName
                })}
          </Typography>
        }
        dialogContent={
          <>
            <TrainingObjectFilters
              defaultValues={defaultFilters}
              trainingObjectTypeOptions={trainingObjectTypeOptions}
              onSubmit={onApplyExternalFilters}
            />
            <Box mt={3} display="flex" minHeight={0} flexDirection="column">
              <Table
                onStateChange={onStateChange}
                columns={memoColumns}
                paginationState={paginationState}
                data={data}
                paginated
                loading={isFetching}
                showSelectAllCheckbox
                initialRowStateAccessor={({
                  original: { daysToComplete, courseType }
                }) => ({
                  dueDate: getDefaultDateForTrainingObject(daysToComplete),
                  courseType
                })}
                showRowSelection
                maxHeight="unset"
                onCheckRow={handleCheckRow}
                rowSelectionDisabled={({ original: { disabled } }) => disabled}
              />
            </Box>
          </>
        }
        dialogActions={
          <ButtonGroup align="left">
            <Button color="primary" onClick={handleClearFilters}>
              {t("cancelBtn")}
            </Button>
            <Button
              color="primary"
              variant="contained"
              disabled={!enableAssignmentButton}
              onClick={() => {
                handleAddAssignmentsClick();
              }}
              name={"addAssignments"}
            >
              {t("employees.assignments.add")}
            </Button>
          </ButtonGroup>
        }
        open={open}
        onClose={handleClearFilters}
      />
      <ConfirmationModal
        open={showConfirmationModal}
        onCancel={() => setShowConfirmationModal(false)}
        assignmentsToAdd={assignmentsToAdd}
        employeeName={employee?.fullName}
        onSubmit={handleCreateNewAssignments}
        isSubmitting={
          validate ? isCreatingValidateAssignments : isCreatingNewAssignments
        }
      />
    </>
  );
};

CreateAssignmentsModal.propTypes = {
  onClose: PropTypes.func,
  open: PropTypes.bool
};

export default CreateAssignmentsModal;
